import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Stack, Paper, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import StackedBarWithCategories from "../../../Charts/StackedBarWithCategories";
import TableChart from "../../../Charts/TableChart";
import { SpeedDialMenu } from "../../../Charts/ChartContainer/SpeedDialMenu";
import {
    stackInsightsSliceSelector,
    SplitModes,
} from "../../../../../../../../slices/Stacks/StackInsights/stackInsightsSlice";
import { getColors } from "../../../utils";

const useStyles = makeStyles(() => ({
    degreesPaper: {
        position: "relative",
        height: "100%",
        width: "100%",
    },
}));

const titleStyles = {
    background: "#F5F5F5",
    height: 45,
};

const chartStyle = {
    mt: 3,
};

const titleTypographyStyle = {
    margin: 0,
    padding: 0,
    width: "100%",
    fontSize: 14,
    textAlign: "center",
};

const categoriesStyle = {
    marginBottom: 13,
    textAlign: "right",
};

export function Section2({ data, stacks }) {
    const [showingCompanies, setShowingCompanies] = useState(true);
    const title = showingCompanies ? "Top Previous Companies/Top Job Titles" : "Top Job Titles/Top Previous Companies";
    const topChartData = showingCompanies ? data.topPreviousCompanies : data.topJobTitles;
    const split = useSelector(stackInsightsSliceSelector.split);
    const show = useSelector(stackInsightsSliceSelector.show);

    const [hovered, setHovered] = useState(false);
    const [page, setPage] = useState(0);
    const barColors = useMemo(
        () =>
            topChartData.length
                ? getColors(
                      topChartData[0].stack.series[0]?.name
                          ? topChartData[0].stack.series
                                .map((serie) => serie.name)
                                .filter((serie) => serie.name !== "Total")
                          : [],
                      show,
                      split,
                  )
                : [],
        [topChartData],
    );

    const classes = useStyles();

    const switchCharts = () => {
        setShowingCompanies(!showingCompanies);
    };

    return (
        <Stack height={328} direction="row" spacing={1.25} mb={1.25}>
            <Stack flex={2} direction="row">
                <Paper
                    sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    <Stack direction="row" gap={0} alignItems="center" height={30} mt={1.75} mb={4.75}>
                        <IconButton size="large" onClick={switchCharts}>
                            <SyncAltIcon />
                        </IconButton>
                        <Typography variant="h5" color="textSecondary">
                            {title}
                        </Typography>
                    </Stack>
                    <Stack maxHeight={0}>
                        <SpeedDialMenu
                            open={hovered}
                            closeMenu={() => setHovered(false)}
                            actionsOffset={{ y: -110, x: 40 }}
                            nextPage={() => setPage(page + 1)}
                            previousPage={() => setPage(page - 1)}
                            page={page}
                            lastPage={Math.ceil(topChartData.length / 2)}
                        />
                    </Stack>
                    <Stack direction="row" flex={1} spacing={9.375} px={9.375}>
                        <Stack flex={1} position="relative">
                            <Stack position="absolute" width="100%" height="100%">
                                {!!topChartData[page * 2] && (
                                    <StackedBarWithCategories
                                        barColors={split === SplitModes.none ? [barColors[0]] : barColors}
                                        barHeight="55%"
                                        showActions={Boolean(false)}
                                        itemsPerPage={5}
                                        stacks={stacks}
                                        title={topChartData[page * 2].stack.name}
                                        titleStyles={titleStyles}
                                        titleTypographyStyle={titleTypographyStyle}
                                        chartStyle={chartStyle}
                                        categories={topChartData[page * 2].categories}
                                        series={topChartData[page * 2].stack.series.filter(
                                            (serie) => serie.name !== "Total",
                                        )}
                                        containerStyle={{ marginTop: 0.25, width: 134 }}
                                        categoriesStyle={categoriesStyle}
                                        categoriesFlex="initial"
                                        isDistributed={false}
                                        paddingBottomPerItem={29.87}
                                        xAxisOffsetPerItem={14.8}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        <Stack flex={1} position="relative">
                            <Stack position="absolute" width="100%" height="100%">
                                {!!topChartData[page * 2 + 1] && (
                                    <StackedBarWithCategories
                                        barColors={split === SplitModes.none ? [barColors[0]] : barColors}
                                        barHeight="55%"
                                        showActions={Boolean(false)}
                                        itemsPerPage={5}
                                        stacks={stacks}
                                        title={topChartData[page * 2 + 1].stack.name}
                                        titleStyles={titleStyles}
                                        titleTypographyStyle={titleTypographyStyle}
                                        chartStyle={chartStyle}
                                        categories={topChartData[page * 2 + 1].categories}
                                        series={topChartData[page * 2 + 1].stack.series.filter(
                                            (serie) => serie.name !== "Total",
                                        )}
                                        containerStyle={{ marginTop: 0.25, width: 147 }}
                                        categoriesStyle={categoriesStyle}
                                        categoriesFlex="initial"
                                        isDistributed={false}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>
            <Stack flex={1}>
                <Paper className={classes.degreesPaper}>
                    <TableChart
                        title="Work Experience per Degree"
                        barHeight="55%"
                        customColors={["#B0C7E9", "#7988E3", "#685FE1", "#6438E3"]}
                        tableData={data.educationDistributionPerCompany.tableChart}
                        categories={stacks.map((item) => item.stack.name)}
                        stacks={stacks}
                        tooltips={[
                            "",
                            "Education Degrees in Male Population",
                            "Education Degrees in Female Population",
                            "Education Degrees in Total",
                        ]}
                        tooltip="%"
                        chartHeight={35}
                    />
                </Paper>
            </Stack>
        </Stack>
    );
}
